import React, { useState, useEffect } from "react";
import { Lock, Eye, EyeOff, User, Loader2 } from "lucide-react";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { PASSWORD_PATTERN } from "../../../utils/constants";
import axios from "axios";
const { REACT_APP_BASE_URL } = process.env;
const { REACT_APP_EMAIL_NOTIFICATION_BASE_URL } = process.env;

// console.log("Base URL:", REACT_APP_BASE_URL);
// console.log(
//   "Email Notification Base URL:",
//   REACT_APP_EMAIL_NOTIFICATION_BASE_URL
// );

interface ResetPasswordFormData {
  password: string;
  password_confirm: string;
  firstName: string;
  lastName: string;
}

interface ValidationStatus {
  password: "validating" | "success" | "warning" | "";
  password_confirm: "validating" | "success" | "warning" | "";
}

/**
 * Trigger Cognito ForgotPassword to send OTP to user's email.
 */
async function sendOtp(data: { email: string }) {
  try {
    const res = await axios.post(`${REACT_APP_EMAIL_NOTIFICATION_BASE_URL}/auth/send-otp`, data);
 //   console.log("OTP sent successfully:", res.data);
    return res.data;
  } catch (err: any) {
    console.error("Error sending OTP:", err.response?.data || err.message);
    throw err.response?.data || { message: "Failed to send OTP" };
  }
}

/**
 * Confirm OTP and set the new password (Cognito ConfirmForgotPassword).
 */
async function confirmOtp(data: {
  email: string;
  code: string;
  password: string;
  platform: string;
}) {
  try {
    const res = await axios.post(
      `${REACT_APP_EMAIL_NOTIFICATION_BASE_URL}/auth/verify-otp-reset`,
      data
    );
 //   console.log("OTP confirmed successfully:", res.data);
    return res.data;
  } catch (err: any) {
    console.error("Error confirming OTP:", err.response?.data || err.message);
    throw err.response?.data || { message: "Failed to confirm OTP" };
  }
}

/**
 * Verify the invite token validity with backend (HMAC-SHA256 token check).
 */
async function verifyInviteToken(invite: string) {
  try {
    const res = await axios.get(`${REACT_APP_BASE_URL}/auth/verify-invite`, {
      params: { invite },
    });
 //   console.log("Invite token verification response:", res);
    return res.data;
  } catch (err: any) {
    console.error(
      "Error verifying invite token:",
      err.response?.data || err.message
    );
    throw err.response?.data || { message: "Invalid or expired token" };
  }
}



const ResetPasswordForm: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Get state from location (passed from login page)
  const locationState = location.state as {
    email?: string;
    isRedirectedFromLogin?: boolean;
  } | null;
  // Use email from invite verification response when available; fall back to location state
  const [email, setEmail] = useState<string>(locationState?.email || "");
  const isRedirectedFromLogin = locationState?.isRedirectedFromLogin || false;
  const [passwordStrength, setPasswordStrength] = useState<string>("strong"); // 'strong', 'medium', 'weak'
  const params = new URLSearchParams(location.search);
  const inviteParam = params.get("invite");

  const [formData, setFormData] = useState<ResetPasswordFormData>({
    password: "",
    password_confirm: "",
    firstName: "",
    lastName: "",
  });
  const [validationStatus, setValidationStatus] = useState<ValidationStatus>({
    password: "",
    password_confirm: "",
  });
  const [showPasswordPolicy, setShowPasswordPolicy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 🔹 Added states for invite + OTP flow
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [isInviteValid, setIsInviteValid] = useState<boolean>(false);
  const [isVerifyingInvite, setIsVerifyingInvite] = useState<boolean>(true);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [timer, setTimer] = useState<number>(0);
  const [canResend, setCanResend] = useState<boolean>(true);
  const [isInputsDisabled, setIsInputsDisabled] = useState<boolean>(false);

  // Timer effect for OTP resend
  useEffect(() => {
    if (timer > 0) {
      const intervalId = setInterval(() => {
        setTimer((prevTime) => prevTime - 1);
      }, 1000);
      return () => clearInterval(intervalId);
    } else if (timer === 0) {
      setCanResend(true);
    }
  }, [timer]);

  // 🔹 Verify invite token on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("invite");
    if (token) {
      setInviteToken(token);
      setIsVerifyingInvite(true);
      (async () => {
        try {
          const res = await verifyInviteToken(token);
         // console.log("Invite token verification result:", res?.data);
          if (res?.data?.valid) {
            setIsInviteValid(true);
            // If backend returns the verified email in res.data.data.email, prefer that
            const verifiedEmail =
              res?.data?.payload?.email ||
              res?.data?.email ||
              locationState?.email ||
              "";
          //  console.log("Verified email from invite token:", verifiedEmail);
            if (verifiedEmail) {
              setEmail(verifiedEmail);
            }
          } else {
            setError("Invalid or expired invite link.");
            setIsInviteValid(false);
          }
        } catch (err) {
          setError("Invalid or expired invite link.");
          setIsInviteValid(false);
        } finally {
          setIsVerifyingInvite(false);
        }
      })();
    } else {
      setIsVerifyingInvite(false);
    }
  }, [location.search]);

  // If invite param is missing from URL, show Access Denied (this is after hooks)
  if (!inviteParam) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600">Access Denied</h2>
          <div className="text-sm text-gray-600">
            This page can only be accessed via a valid invite link.
          </div>
          <Link to="/login" className="text-[#EB1C37] underline mt-4 block">
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  // Show loader while verifying invite token
  if (isVerifyingInvite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#EB1C37] mx-auto mb-4" />
          <p className="text-sm text-gray-600">Verifying invite link...</p>
        </div>
      </div>
    );
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (error) {
      setError(null);
    }
  };

  const handleResendOtp = async () => {
    if (!canResend) return;
    await handleSendOtp();
  };

  const validatePassword = (password: string) => {
    if (!password) {
      setValidationStatus((prev) => ({ ...prev, password: "" }));
      setShowPasswordPolicy(false);
      return;
    }

    setValidationStatus((prev) => ({ ...prev, password: "validating" }));
    setShowPasswordPolicy(true);

    setTimeout(() => {
      let strength: "strong" | "medium" | "weak" = "weak";

      if (password.length >= 12 && PASSWORD_PATTERN.test(password)) {
        strength = "strong";
      } else if (password.length >= 8 && PASSWORD_PATTERN.test(password)) {
        strength = "medium";
      } else {
        strength = "weak";
      }

      setPasswordStrength(strength);

      if (strength === "strong") {
        setValidationStatus((prev) => ({ ...prev, password: "success" }));
        setShowPasswordPolicy(false);
      } else {
        setValidationStatus((prev) => ({ ...prev, password: "warning" }));
        if (!showPasswordPolicy) {
          setShowPasswordPolicy(true);
        }
      }
    }, 500);
  };

  const validateConfirmPassword = (confirmPassword: string) => {
    if (!confirmPassword) {
      setValidationStatus((prev) => ({ ...prev, password_confirm: "" }));
      return;
    }

    setValidationStatus((prev) => ({
      ...prev,
      password_confirm: "validating",
    }));

    setTimeout(() => {
      if (
        confirmPassword === formData.password &&
        passwordStrength === "strong" &&
        confirmPassword.length >= 12 &&
        validationStatus.password === "success"
      ) {
        setValidationStatus((prev) => ({
          ...prev,
          password_confirm: "success",
        }));
      } else {
        setValidationStatus((prev) => ({
          ...prev,
          password_confirm: "warning",
        }));
      }
    }, 500);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    handleInputChange(e);
    validatePassword(value);

    if (formData.password_confirm) {
      validateConfirmPassword(formData.password_confirm);
    }
  };

  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    handleInputChange(e);
    validateConfirmPassword(value);
  };

  // 🔹 Modified handleSubmit for OTP initiation
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      if (!isInviteValid) {
        setError("Invite link is invalid or expired.");
        return;
      }

      if (formData.password !== formData.password_confirm) {
        setError("Passwords do not match");
        return;
      }

      if (formData.password.length < 12) {
        setError("Password must be at least 12 characters long");
        return;
      }

      if (!PASSWORD_PATTERN.test(formData.password)) {
        setError(
          "Password must contain at least 1 number, 1 special character, 1 uppercase letter, and 1 lowercase letter"
        );
        return;
      }

      setIsLoading(true);
      await sendOtp({ email }); // 🔹 Send OTP to user email
      setOtpSent(true);
      setError(null);
    } catch (err) {
      setError("Failed to send OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // 🔹 Handle OTP confirmation and final password reset
  const handleVerifyOtp = async () => {
    try {
      setIsVerifying(true);
      const res = await confirmOtp({
        email,
        code: otp,
        password: formData.password,
        platform: "Data360",
      });
      if (res?.success) {
        setOtpVerified(true);
        // Show final success message — replace the page UI with this message
        setSuccessMessage("Password updated.");
        navigate("/login");
      } else {
        setError("Invalid OTP. Please try again.");
      }
    } catch (err) {
      setError("Verification failed. Please retry.");
    } finally {
      setIsVerifying(false);
    }
  };

  const getValidationIcon = (status: string) => {
    switch (status) {
      case "success":
        return (
          <div className="absolute inset-y-0 right-12 flex items-center">
            <div className="h-5 w-5 text-green-500">✓</div>
          </div>
        );
      case "warning":
        return (
          <div className="absolute inset-y-0 right-12 flex items-center">
            <div className="h-5 w-5 text-red-500">✗</div>
          </div>
        );
      case "validating":
        return (
          <div className="absolute inset-y-0 right-12 flex items-center">
            <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
          </div>
        );
      default:
        return null;
    }
  };

  const getInputBorderColor = (status: string) => {
    switch (status) {
      case "success":
        return " focus:";
      case "warning":
        return " focus:";
      case "validating":
        return " focus:";
      default:
        return "border-gray-300 focus:border-[#EB1C37]";
    }
  };

  // 🔹 Show invalid invite message if needed
  if (!isInviteValid && inviteToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600">
            Invalid or Expired Invite Link
          </h2>
          <div className="text-sm text-gray-600">You can close this tab.</div>
          {/* <Link to="/login" className="text-[#EB1C37] underline mt-4 block">
            Back to Login
          </Link> */}
        </div>
      </div>
    );
  }

  // If OTP was verified successfully, replace the page with a final message
  if (otpVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center bg-white p-8 rounded shadow-sm">
          <div className="text-2xl font-semibold text-green-700 mb-2">
            {successMessage || "Password updated"}
          </div>
          <div className="text-sm text-gray-600">You can close this tab.</div>
        </div>
      </div>
    );
  }

  const handleSendOtp = async () => {
    try {
      setIsLoading(true);
      await sendOtp({ email });
      setOtpSent(true);
      setIsInputsDisabled(true);
      setOtp(""); // Reset OTP input boxes

      // Start 30s timer
      setTimer(30);
      setCanResend(false);

      setSuccessMessage("OTP sent successfully!");
      
      // Focus the first OTP input after reset
      setTimeout(() => {
        const firstInput = document.getElementById("otp-0");
        firstInput && firstInput.focus();
      }, 0);
    } catch (err: any) {
      setError(err.message || "Failed to send OTP");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 text-[#EB1C37] mb-4">
            <Lock className="h-12 w-12" />
          </div>
          <h2 className="text-2xl font-bold tracking-tight bg-gradient-to-r from-[#EB1C37] to-rose-500 bg-clip-text text-transparent">
            Change Password
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Enter your new password and personal information
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow-sm sm:rounded-lg sm:px-10">
            <form className="space-y-6" onSubmit={handleSubmit}>
              {/* Disabled Email Input */}
              {!otpSent && (
                <>
                  {/* <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Email
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={email}
                        disabled
                        className="pl-9 bg-gray-100 text-gray-500 cursor-not-allowed"
                      />
                    </div>
                  </div> */}

                  <div className="relative">
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium text-gray-700"
                    >
                      New Password
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-gray-400" />
                      </div>
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="new-password"
                        required
                        className={`pl-9 pr-20 ${getInputBorderColor(
                          validationStatus.password
                        )} [&::-ms-reveal]:hidden [&::-ms-clear]:hidden [&::-webkit-credentials-auto-fill-button]:hidden [&::-webkit-credentials-auto-fill-button]:hover:bg-none [&::-webkit-credentials-auto-fill-button]:active:bg-none [&::-webkit-credentials-auto-fill-button]:focus:bg-none`}
                        placeholder="Enter new password"
                        value={formData.password}
                        onChange={handlePasswordChange}
                        onFocus={() => {
                          if (validationStatus.password !== "success") {
                            setShowPasswordPolicy(true);
                          } else {
                            setShowPasswordPolicy(false);
                          }
                        }}
                        onBlur={() => {
                          if (validationStatus.password !== "success") {
                            setTimeout(() => setShowPasswordPolicy(false), 200);
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5 text-gray-400" />
                        ) : (
                          <Eye className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>

                    {/* Password Strength Bar */}
                    {formData.password.length > 0 && (
                      <div className="mt-2 flex items-center">
                        {[0, 1, 2, 3, 4].map((idx) => (
                          <div
                            key={idx}
                            className={`h-1.5 w-8 mx-0.5 rounded transition-colors duration-300 ${
                              passwordStrength === "strong"
                                ? "bg-green-500"
                                : passwordStrength === "medium"
                                ? idx < 3
                                  ? "bg-yellow-400"
                                  : "bg-gray-200"
                                : idx < 1
                                ? "bg-red-500"
                                : "bg-gray-200"
                            }`}
                          />
                        ))}
                        <span
                          className={`ml-2 text-xs font-medium ${
                            passwordStrength === "strong"
                              ? "text-green-600"
                              : passwordStrength === "medium"
                              ? "text-yellow-600"
                              : "text-red-600"
                          }`}
                        >
                          {passwordStrength.charAt(0).toUpperCase() +
                            passwordStrength.slice(1)}
                        </span>
                      </div>
                    )}

                    {showPasswordPolicy &&
                      formData.password.length > 0 &&
                      validationStatus.password !== "success" && (
                        <div className="mt-2 p-3 bg-blue-50 border rounded-md">
                          <h6 className="text-sm font-medium text-blue-900 mb-2">
                            Your password should contain:
                          </h6>
                          <ul className="text-xs text-blue-800 space-y-1">
                            <li>• Minimum length of 12 character(s)</li>
                            <li>• Contains at least 1 number</li>
                            <li>• Contains at least 1 special character</li>
                            <li>• Contains at least 1 uppercase letter</li>
                            <li>• Contains at least 1 lowercase letter</li>
                          </ul>
                        </div>
                      )}
                  </div>

                  <div>
                    <label
                      htmlFor="password_confirm"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Confirm New Password
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-gray-400" />
                      </div>
                      <Input
                        id="password_confirm"
                        name="password_confirm"
                        type={showConfirmPassword ? "text" : "password"}
                        autoComplete="new-password"
                        required
                        className={`pl-9 pr-20 ${getInputBorderColor(
                          validationStatus.password_confirm
                        )} [&::-ms-reveal]:hidden [&::-ms-clear]:hidden [&::-webkit-credentials-auto-fill-button]:hidden [&::-webkit-credentials-auto-fill-button]:hover:bg-none [&::-webkit-credentials-auto-fill-button]:active:bg-none [&::-webkit-credentials-auto-fill-button]:focus:bg-none`}
                        placeholder="Confirm new password"
                        value={formData.password_confirm}
                        onChange={handleConfirmPasswordChange}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-5 w-5 text-gray-400" />
                        ) : (
                          <Eye className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>

                    {/* Show password mismatch error only when first password is valid */}
                    {validationStatus.password === "success" &&
                      formData.password_confirm &&
                      formData.password !== formData.password_confirm && (
                        <div className="mt-2 text-sm text-readable-red bg-[rgba(196,0,31,0.06)] rounded-md p-2">
                          Passwords do not match
                        </div>
                      )}
                  </div>
                </>
              )}

              {error && (
                <div className="text-sm text-readable-red bg-[rgba(196,0,31,0.06)] rounded-md p-3">
                  {error}
                </div>
              )}

              {!otpSent && (
                <Button
                  type="submit"
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#EB1C37] hover:bg-[#EB1C37]/90"
                  disabled={
                    isLoading ||
                    !(
                      passwordStrength === "strong" &&
                      formData.password === formData.password_confirm
                    )
                  }
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                      Sending OTP...
                    </>
                  ) : (
                    "Send OTP to Email"
                  )}
                </Button>
              )}

              {/* 🔹 OTP section appears after OTP is sent */}
              {otpSent && (
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="otp"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Enter OTP
                    </label>

                    {/* OTP input boxes */}
                    <div className="flex justify-between gap-2">
                      {Array.from({ length: 6 }).map((_, index) => (
                        <Input
                          key={index}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          className={`w-12 h-12 text-center text-lg border rounded-md focus:outline-none ${
                            otp.length === 6
                              ? ""
                              : "border-gray-300 focus:"
                          }`}
                          value={otp[index] || ""}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/, "");
                            const newOtp = otp.split("");
                            newOtp[index] = value;
                            setOtp(newOtp.join(""));
                            if (value && index < 5) {
                              const next = document.getElementById(
                                `otp-${index + 1}`
                              );
                              next && next.focus();
                            }
                          }}
                          onPaste={
                            index === 0
                              ? (e) => {
                                  e.preventDefault();
                                  const pastedText =
                                    e.clipboardData.getData("text");
                                  const digits = pastedText.replace(/\D/g, "");
                                  const digitsToUse = digits.slice(0, 6);
                                  setOtp(digitsToUse);
                                  // Focus the last filled input or next empty input
                                  const focusIndex = Math.min(
                                    digitsToUse.length - 1,
                                    5
                                  );
                                  setTimeout(() => {
                                    const targetInput = document.getElementById(
                                      `otp-${focusIndex}`
                                    );
                                    targetInput && targetInput.focus();
                                  }, 0);
                                }
                              : undefined
                          }
                          onKeyDown={(e) => {
                            if (
                              e.key === "Backspace" &&
                              !otp[index] &&
                              index > 0
                            ) {
                              const prev = document.getElementById(
                                `otp-${index - 1}`
                              );
                              prev && prev.focus();
                            }
                          }}
                          id={`otp-${index}`}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Verify OTP Button */}
                  <Button
                    type="button"
                    className="w-full bg-[#EB1C37] text-white py-2"
                    onClick={handleVerifyOtp}
                    disabled={isVerifying || otp.length < 6}
                  >
                    {isVerifying ? (
                      <>
                        <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                        Verifying...
                      </>
                    ) : (
                      "Verify OTP & Reset Password"
                    )}
                  </Button>

                  {/* Resend OTP Section (visible immediately but disabled for 30s) */}
                  <div className="text-center text-sm text-gray-600">
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={!canResend}
                      className={`font-medium ${
                        canResend
                          ? "text-[#EB1C37] hover:underline"
                          : "text-gray-400 cursor-not-allowed"
                      }`}
                    >
                      {canResend ? "Resend OTP" : `Resend OTP in ${timer}s`}
                    </button>
                  </div>
                </div>
              )}

              {/* successMessage is shown on the final page after verification; no inline notification here */}

              {/* <div>
                <Button type="button" variant="outline" className="w-full" asChild>
                  <Link to="/login">Back to Login</Link>
                </Button>
              </div> */}
            </form>
          </div>
        </div>
      </div>

      <div className="mt-8 text-center text-xs text-gray-500">
        <span>Pioneered by CRC Evans &copy;{new Date().getFullYear()} | </span>
        <a href="#" className="text-gray-400 cursor-not-allowed">
          Terms & Conditions
        </a>
        <span> | </span>
        <a
          href="https://www.crcevans.com/privacy-policy/"
          target="_blank"
          rel="noreferrer noopener"
          className="text-[#EB1C37] hover:underline"
        >
          Privacy Policy
        </a>
      </div>
    </div>
  );
};

export default ResetPasswordForm;
